// src/utils/licenseConfig.ts
// Frontend-side license configuration.
// Mirrors the API's licenseService.ts — types, constants, and mock token
// are duplicated here so the web app has no cross-package dependency on the API.
//
// In production, the web app calls GET /api/system/license to get live status.
// This file provides the fallback mock and the UI constants.

// ── Types ──────────────────────────────────────────────────────────────────

export type LicensePlan   = 'starter' | 'professional' | 'business' | 'enterprise'
export type LicenseStatus = 'active' | 'trial' | 'suspended' | 'expired'

export interface LicenseToken {
  tenantId:     string
  companyName:  string
  plan:         LicensePlan
  features:     string[]
  maxUsers:     number | null
  maxJobs:      number | null
  validUntil:   string
  status:       LicenseStatus
  issuedAt:     string
  issuedBy:     string
  tokenVersion: number
}

// ── Feature flag keys ──────────────────────────────────────────────────────

export const FEATURES = {
  WHS_FULL:       'whs-full',
  AI_AGENTS:      'ai-agents',
  XERO:           'xero',
  STP:            'stp',
  MOBILE:         'mobile',
  MULTI_ENTITY:   'multi-entity',
  SUPPLIER_INTEL: 'supplier-intel',
  CUSTOM_BRAND:   'custom-brand',
  API_ACCESS:     'api-access',
} as const

export type FeatureKey = typeof FEATURES[keyof typeof FEATURES]

// ── Plan feature matrix ────────────────────────────────────────────────────

export const PLAN_FEATURES: Record<LicensePlan, string[]> = {
  starter:      ['whs-full'],
  professional: ['whs-full', 'ai-agents', 'xero', 'stp', 'mobile', 'supplier-intel', 'custom-brand'],
  business:     ['whs-full', 'ai-agents', 'xero', 'stp', 'mobile', 'supplier-intel', 'custom-brand', 'multi-entity'],
  enterprise:   ['whs-full', 'ai-agents', 'xero', 'stp', 'mobile', 'supplier-intel', 'custom-brand', 'multi-entity', 'api-access'],
}

export const PLAN_LIMITS: Record<LicensePlan, { maxUsers: number | null; maxJobs: number | null }> = {
  starter:      { maxUsers: 3,    maxJobs: 5    },
  professional: { maxUsers: 15,   maxJobs: 20   },
  business:     { maxUsers: 50,   maxJobs: null },
  enterprise:   { maxUsers: null, maxJobs: null },
}

// ── Prototype mock token ───────────────────────────────────────────────────

export const MOCK_LICENSE_TOKEN: LicenseToken = {
  tenantId:     'mcb-civil-electrical',
  companyName:  'MCB Civil Electrical Pty Ltd',
  plan:         'professional',
  features:     PLAN_FEATURES.professional,
  maxUsers:     PLAN_LIMITS.professional.maxUsers,
  maxJobs:      PLAN_LIMITS.professional.maxJobs,
  validUntil:   '2027-08-08',
  status:       'active',
  issuedAt:     '2026-08-08',
  issuedBy:     'trafficprocure-authority-v1',
  tokenVersion: 1,
}

// ── Helper functions ───────────────────────────────────────────────────────

export function isLicenseValid(token: LicenseToken): boolean {
  if (token.status !== 'active' && token.status !== 'trial') return false
  return new Date(token.validUntil) > new Date()
}

export function isReadOnly(token: LicenseToken): boolean {
  return !isLicenseValid(token)
}

export function isFeatureEnabled(token: LicenseToken, feature: FeatureKey): boolean {
  if (!isLicenseValid(token)) return false
  return token.features.includes(feature)
}

export function daysUntilExpiry(token: LicenseToken): number {
  return Math.ceil((new Date(token.validUntil).getTime() - Date.now()) / 86400000)
}

export function planLabel(plan: LicensePlan): string {
  return { starter: 'Starter', professional: 'Professional', business: 'Business', enterprise: 'Enterprise' }[plan]
}
